import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import ScrollToTopOnMount from "../Components/ScrollToTop";
import Footer from "../UI/Footer/Footer";
import NavBar from "../UI/NavBar/NavBar";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { cn } from "../lib/utils";
import "../UI/Grid.css";
import "./dedicated-team.css";

const APPOINTMENT_URL =
  "https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ1uHmQd3vieliyTtHcA_U4Zzs9Si9Evo12FkwP2oYQWihGVDxU_wJYh6J6YZKK8NC9Ha0MoRyQ6";

const audiences = [
  {
    index: "01",
    title: "Founders",
    body: "Turn a sharp idea into an MVP without hiring a full product and engineering team before you have product-market fit.",
  },
  {
    index: "02",
    title: "Scale-ups",
    body: "Add senior delivery capacity when your roadmap outpaces headcount. Keep velocity high without long hiring cycles.",
  },
  {
    index: "03",
    title: "Established companies",
    body: "Modernise products, launch new digital channels, or unblock complex programmes with a pod that owns outcomes.",
  },
];

const offerPoints = [
  {
    title: "Dedicated, not disposable",
    body: "The same people learn your domain, your stack, and your standards.",
  },
  {
    title: "Product lead included",
    body: "Someone owns priorities, trade-offs, and the conversation with your stakeholders.",
  },
  {
    title: "Monthly SLA you can plan around",
    body: "Agreed capacity and cadence so roadmap planning stays predictable.",
  },
  {
    title: "Problem-led, industry-aware",
    body: "We engage around the business problem: fintech, ecommerce, recruitment, and similar complex domains.",
  },
];

const steps = [
  {
    title: "Align",
    body: "We clarify the problem, success criteria, constraints, and the shape of the first delivery slice.",
  },
  {
    title: "Staff",
    body: "We assemble your pod: product lead plus engineers matched to the stack and the complexity of the work.",
  },
  {
    title: "Ship",
    body: "We deliver against the SLA with visible progress, regular demos, and production-minded engineering.",
  },
  {
    title: "Scale",
    body: "Grow capacity, extend the roadmap, or taper down as needs change. The relationship flexes with you.",
  },
];

const inclusions = [
  {
    title: "Dedicated engineers",
    body: "Hands-on builders who stay with the product long enough to matter.",
  },
  {
    title: "Embedded product lead",
    body: "Prioritisation, stakeholder alignment, and a clear delivery narrative.",
  },
  {
    title: "Monthly SLA",
    body: "Agreed capacity and cadence you can put into planning conversations.",
  },
  {
    title: "Idea to MVP or production",
    body: "Discovery through to shippable product, with quality that survives contact with users.",
  },
  {
    title: "Transparent collaboration",
    body: "Shared tools, visible backlog, and direct access to the people doing the work.",
  },
  {
    title: "Flexible on-ramps",
    body: "Start hourly, with hour blocks, or as a defined one-time project before you commit to a pod.",
  },
];

const tiers = [
  {
    name: "Starter pod",
    amount: "£2,500",
    period: "/ month",
    description: "Best for an MVP or early product.",
    features: [
      "About 1 dedicated engineer, plus a shared product lead",
      "Weekly cadence and Slack access",
      "A clear monthly SLA",
    ],
    featured: false,
  },
  {
    name: "Growth pod",
    amount: "£5,500",
    period: "/ month",
    description: "Best for active roadmaps.",
    features: [
      "About 2 to 3 engineers, plus a named product lead",
      "Priority support within the SLA",
      "Capacity that can flex with the roadmap",
    ],
    featured: true,
  },
  {
    name: "Custom SLA",
    amount: "£9,500",
    period: "/ month",
    description: "Bespoke, for multi-stream or regulated work.",
    features: [
      "Tailored team shape and seniority",
      "Multi-stream coordination",
      "Security and compliance alignment",
    ],
    featured: false,
  },
];

const onRamps = [
  {
    name: "Hourly",
    label: "from",
    amount: "£45",
    period: "/ hour",
    detail: "Subsidised UK rate. A lighter way in before a pod.",
    priceLabel: "from £45 per hour",
  },
  {
    name: "Monthly hour block",
    label: "from",
    amount: "£1,600",
    period: "/ month",
    detail: "40 hours. Steady capacity before you commit to a pod.",
    priceLabel: "from £1,600 per month for 40 hours",
  },
  {
    name: "One-time project",
    label: "fixed scope",
    amount: "Fixed quote",
    period: "after discovery",
    detail: "A fixed scope, priced once the work is clear.",
    priceLabel: "fixed scope quote after discovery",
  },
];

const sampleProjects = [
  {
    name: "Jazzy's Burger",
    href: "/caseStudy/jazzysburger",
    line: "Needed multi-location QSR ordering across Nigerian cities. Shipped apps, web checkout, payments, and admin.",
  },
  {
    name: "Eve After Dark",
    href: "/caseStudy/ead",
    line: "Needed cloud-kitchen ordering and ops in Nigeria and the UK. Shipped branch, stock, and fulfilment admin.",
  },
  {
    name: "Vault Hill",
    href: "/blog/vaultHill",
    line: "Needed a first public site for a complex metaverse product. Designed and built the marketing website.",
  },
];

const podMembers = [
  {
    initials: "PL",
    name: "Product lead",
    detail: "Roadmap, priorities, stakeholder rhythm",
    tag: "Lead",
    avatarClass: "bg-primary",
  },
  {
    initials: "SE",
    name: "Senior engineer",
    detail: "Architecture, delivery, code quality",
    tag: "Core",
    avatarClass: "bg-black",
  },
  {
    initials: "+1",
    name: "Additional capacity",
    detail: "Scale the pod as the roadmap grows",
    tag: "Flex",
    avatarClass: "bg-[#292929]",
  },
];

function AppointmentButton({ variant = "default", className, children }) {
  return (
    <Button variant={variant} size="lg" className={className} asChild>
      <a href={APPOINTMENT_URL} target="_blank" rel="noopener noreferrer">
        {children || "Book an appointment"}
      </a>
    </Button>
  );
}

function Section({ id, titleId, eyebrow, title, lead, className, children }) {
  return (
    <section
      id={id}
      aria-labelledby={titleId}
      className={cn("py-20 md:py-24", className)}
    >
      <div className="uigrid">
        <div className="uigrid-12">
          <div className="mb-10 max-w-3xl">
            <p className="mb-3 text-sm font-medium text-primary">{eyebrow}</p>
            <h2 id={titleId} className="text-4xl leading-tight md:text-5xl">
              {title}
            </h2>
            {lead ? (
              <p className="mt-4 text-lg leading-8 text-[#525252]">{lead}</p>
            ) : null}
          </div>
          {children}
        </div>
      </div>
    </section>
  );
}

function CheckItem({ title, children }) {
  return (
    <li className="flex gap-3">
      <span
        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-white"
        aria-hidden="true"
      >
        <Check className="h-3.5 w-3.5" />
      </span>
      <div>
        <p className="font-display text-lg text-black">{title}</p>
        <p className="mt-1 text-base leading-7 text-[#525252]">{children}</p>
      </div>
    </li>
  );
}

const DedicatedTeam = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title =
      "Your dedicated product team, without the hire | Awa Digital";

    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description
      ? description.getAttribute("content")
      : null;
    if (description) {
      description.setAttribute(
        "content",
        "Awa Digital: software development as a service. Dedicated engineers and a product lead on a monthly SLA. From idea to MVP and production-ready apps."
      );
    }

    return () => {
      document.title = previousTitle;
      if (description && previousDescription != null) {
        description.setAttribute("content", previousDescription);
      }
    };
  }, []);

  return (
    <div>
      <ScrollToTopOnMount />
      <NavBar />
      <main className="dedicated-pod" lang="en-GB">
        <section className="pb-16 pt-12 md:pb-24 md:pt-16" aria-labelledby="hero-heading">
          <div className="uigrid">
            <div className="uigrid-12">
              <p className="mb-4 text-sm font-medium text-primary">
                Software development as a service
              </p>
              <h1
                id="hero-heading"
                className="max-w-4xl text-[40px] leading-[1.15] md:text-[64px] md:leading-[1.08]"
              >
                Your dedicated product team, without the hire.
              </h1>

              <div className="mt-10 grid items-start gap-12 md:grid-cols-2">
                <div>
                  <p className="max-w-xl text-lg leading-8 text-[#292929]">
                    We embed engineers and a product lead into your business.
                    From idea to MVP or production-ready apps, on a clear
                    monthly SLA. Not freelancers. Not a black-box agency.
                  </p>
                  <div className="mt-8 flex flex-wrap items-center gap-6">
                    <AppointmentButton />
                    <Button variant="link" size="lg" asChild>
                      <a href="#how-it-works">See how a pod works</a>
                    </Button>
                  </div>
                  <ul className="mt-10 grid gap-5">
                    <li>
                      <p className="font-display text-base text-black">
                        Founders to enterprises
                      </p>
                      <p className="mt-1 text-sm leading-6 text-[#525252]">
                        Built for complex product work
                      </p>
                    </li>
                    <li>
                      <p className="font-display text-base text-black">
                        Problem-led engagement
                      </p>
                      <p className="mt-1 text-sm leading-6 text-[#525252]">
                        Fintech, ecommerce, recruitment, and more
                      </p>
                    </li>
                    <li>
                      <p className="font-display text-base text-black">
                        Flexible on-ramps
                      </p>
                      <p className="mt-1 text-sm leading-6 text-[#525252]">
                        Hourly, blocks, or one-time projects
                      </p>
                    </li>
                  </ul>
                </div>

                <Card aria-label="Example delivery pod">
                  <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
                    <p className="font-display text-lg text-black">
                      Your delivery pod
                    </p>
                    <Badge>SLA active</Badge>
                  </CardHeader>
                  <CardContent className="grid gap-0">
                    {podMembers.map((member) => (
                      <div
                        key={member.name}
                        className="flex items-center gap-3 border-t border-[#e8e8e8] py-4"
                      >
                        <div
                          className={cn(
                            "flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs text-white",
                            member.avatarClass
                          )}
                          aria-hidden="true"
                        >
                          {member.initials}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-base text-black">{member.name}</p>
                          <p className="text-sm leading-6 text-[#525252]">
                            {member.detail}
                          </p>
                        </div>
                        <Badge variant="outline">{member.tag}</Badge>
                      </div>
                    ))}
                    <div className="border-t border-[#e8e8e8] pt-4">
                      <div className="mb-2 flex items-center justify-between text-sm">
                        <span>Monthly capacity</span>
                        <span className="text-primary">On track</span>
                      </div>
                      <div
                        className="h-2 w-full bg-[#ececec]"
                        aria-hidden="true"
                      >
                        <div className="h-2 w-[72%] bg-primary" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <Section
          id="who-it-is-for"
          titleId="audience-heading"
          eyebrow="Who it is for"
          title="Built for teams that need real product capacity"
          lead="Whether you are validating an idea or scaling an established platform, you get a dedicated pod that works as an extension of your team."
          className="bg-[#faf0e6]"
        >
          <div className="mb-8 flex flex-wrap gap-2">
            <Badge variant="outline">Fintech</Badge>
            <Badge variant="outline">Ecommerce</Badge>
            <Badge variant="outline">Recruitment</Badge>
            <Badge variant="outline">And similar domains</Badge>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {audiences.map((audience) => (
              <Card key={audience.title} className="h-full bg-white">
                <CardHeader>
                  <p className="text-sm font-medium text-primary">
                    {audience.index}
                  </p>
                  <CardTitle>{audience.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-base leading-7 text-[#525252]">
                    {audience.body}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </Section>

        <Section
          id="offer"
          titleId="offer-heading"
          eyebrow="The offer"
          title="Software development as a service"
          lead="SDaaS means you subscribe to a dedicated pod: engineers plus a product lead, working against a monthly SLA. You get continuity, accountability, and clear capacity. Not a rotating cast of freelancers, and not an opaque agency process you cannot see into."
        >
          <ul className="grid gap-6 md:grid-cols-2">
            {offerPoints.map((point) => (
              <CheckItem key={point.title} title={point.title}>
                {point.body}
              </CheckItem>
            ))}
          </ul>
        </Section>

        <Section
          id="how-it-works"
          titleId="how-heading"
          eyebrow="How it works"
          title="Align, staff, ship, scale"
          lead="A simple rhythm from first conversation to sustained delivery."
          className="bg-[rgb(255,219,219)]"
        >
          <div className="grid gap-6 md:grid-cols-4">
            {steps.map((step, index) => (
              <Card key={step.title} className="h-full bg-white">
                <CardHeader>
                  <p className="text-sm font-medium text-primary">
                    0{index + 1}
                  </p>
                  <CardTitle>{step.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-base leading-7 text-[#525252]">
                    {step.body}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </Section>

        <Section
          id="what-you-get"
          titleId="get-heading"
          eyebrow="What you get"
          title="Everything a product team needs to move"
          lead="Capacity, ownership, and working software. Not slide decks and radio silence."
        >
          <ul className="grid gap-8 md:grid-cols-2">
            {inclusions.map((item) => (
              <CheckItem key={item.title} title={item.title}>
                {item.body}
              </CheckItem>
            ))}
          </ul>
        </Section>

        <section
          id="sample-projects"
          aria-labelledby="projects-heading"
          className="border-y border-[#e8e8e8] bg-white py-10 md:py-12"
        >
          <div className="uigrid">
            <div className="uigrid-12">
              <p className="mb-2 text-sm font-medium text-primary">
                Sample projects
              </p>
              <h2
                id="projects-heading"
                className="text-2xl leading-tight md:text-3xl"
              >
                Work already in production
              </h2>
              <div className="mt-6 grid gap-4 md:grid-cols-3">
                {sampleProjects.map((project) => (
                  <Card key={project.name} className="bg-white">
                    <CardHeader className="gap-2 p-5">
                      <CardTitle className="text-xl">
                        <Link
                          to={project.href}
                          className="text-black hover:underline"
                        >
                          {project.name}
                        </Link>
                      </CardTitle>
                      <CardDescription className="leading-6">
                        {project.line}
                      </CardDescription>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        <Section
          id="pricing"
          titleId="pricing-heading"
          eyebrow="Pricing"
          title="Pods sized to the work"
          lead="Introductory UK pricing, set to stay accessible and below typical western agency fees. Talk to us for a proposal. The final quote depends on scope and seniority."
          className="bg-[#faf0e6]"
        >
          <div className="grid items-stretch gap-6 md:grid-cols-3">
            {tiers.map((tier) => (
              <Card
                key={tier.name}
                className={cn(
                  "flex h-full flex-col bg-white",
                  tier.featured && "border-2 border-primary"
                )}
              >
                <CardHeader>
                  {tier.featured ? (
                    <Badge className="w-fit">Most chosen</Badge>
                  ) : null}
                  <CardTitle>{tier.name}</CardTitle>
                  <p
                    className="mt-2 text-black"
                    aria-label={`from ${tier.amount} per month`}
                  >
                    <span className="block text-sm font-medium text-primary">
                      from
                    </span>
                    <span className="mt-1 block font-display text-5xl leading-none">
                      {tier.amount}
                    </span>
                    <span className="mt-2 block text-base text-[#292929]">
                      {tier.period}
                    </span>
                  </p>
                  <CardDescription>{tier.description}</CardDescription>
                </CardHeader>
                <CardContent className="flex-1">
                  <ul className="grid gap-3">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <Check
                          className="mt-1 h-4 w-4 shrink-0 text-primary"
                          aria-hidden="true"
                        />
                        <span className="text-base leading-6 text-[#292929]">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter className="flex-col items-stretch gap-2">
                  <AppointmentButton
                    variant={tier.featured ? "default" : "outline"}
                    className="w-full"
                  />
                  <Button variant="link" className="h-auto w-full" asChild>
                    <Link to="/contactUs">Talk to us</Link>
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          <div className="mt-12">
            <h3 className="text-3xl leading-tight">On-ramps</h3>
            <p className="mt-3 max-w-2xl text-lg leading-8 text-[#525252]">
              Start smaller, at the same introductory UK rates, and grow when
              the fit is clear.
            </p>
            <div className="mt-6 grid items-stretch gap-6 md:grid-cols-3">
              {onRamps.map((onRamp) => (
                <Card key={onRamp.name} className="flex h-full flex-col bg-white">
                  <CardHeader>
                    <CardTitle className="text-xl">{onRamp.name}</CardTitle>
                    <p className="mt-2 text-black" aria-label={onRamp.priceLabel}>
                      <span className="block text-sm font-medium text-primary">
                        {onRamp.label}
                      </span>
                      <span className="mt-1 block font-display text-4xl leading-none">
                        {onRamp.amount}
                      </span>
                      <span className="mt-2 block text-base text-[#292929]">
                        {onRamp.period}
                      </span>
                    </p>
                  </CardHeader>
                  <CardContent className="flex-1">
                    <p className="text-base leading-7 text-[#525252]">
                      {onRamp.detail}
                    </p>
                  </CardContent>
                  <CardFooter>
                    <AppointmentButton variant="outline" className="w-full" />
                  </CardFooter>
                </Card>
              ))}
            </div>
          </div>

          <p className="mt-8 max-w-3xl text-base leading-7 text-[#525252]">
            Rates shown are introductory UK pricing. A final quote depends on
            scope and seniority.{" "}
            <Link to="/contactUs" className="text-black underline">
              Talk to us
            </Link>{" "}
            or book an appointment and we will map the right start.
          </p>
        </Section>

        <section
          id="contact"
          aria-labelledby="cta-heading"
          className="bg-black py-20 text-white md:py-24"
        >
          <div className="uigrid">
            <div className="uigrid-12">
              <div className="max-w-3xl">
                <p className="mb-3 text-sm font-medium text-primary">
                  Next step
                </p>
                <h2
                  id="cta-heading"
                  className="text-4xl leading-tight text-white md:text-5xl"
                >
                  Book an appointment
                </h2>
                <p className="mt-4 text-lg leading-8 text-white">
                  Tell us what you are building. A short intro is enough to map
                  whether a pod, a project, or a lighter on-ramp is the right
                  start.
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-6">
                  <AppointmentButton />
                  <Button
                    variant="outline"
                    size="lg"
                    className="border-white text-white hover:bg-white hover:text-black"
                    asChild
                  >
                    <a href="#how-it-works">See how a pod works</a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default DedicatedTeam;
